/**
 * ============================================================================
 * PATRON PANOSU — biYapayZeka  [2026-08-09]
 * ----------------------------------------------------------------------------
 * AYRI bir bilesendir. Mevcut "Genel Istatistikler" bolumune HIC dokunmaz;
 * onun USTUNE eklenir. Bir sorun olsa bile eski panel oldugu gibi calisir.
 *
 * Veri kaynagi: /api/dashboard/patron  ve  /api/dashboard/patron/liste
 * (ikisi de giris + admin korumali)
 * ============================================================================
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Typography, Divider, Stack, Card, CardContent, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress, Alert,
} from "@mui/material";
import {
  BarChart as RcBarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RcTooltip, Legend as RcLegend, ResponsiveContainer,
} from "recharts";

/* ----------------------------- yardimcilar ----------------------------- */
const TL = (n) =>
  (Number(n) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " ₺";
const TL2 = (n) =>
  (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const SAY = (n) => (Number(n) || 0).toLocaleString("tr-TR");
const TARIH = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch (e) { return "-"; }
};

const RENK = {
  ciro: "#12a05c", kayit: "#2f6df6", deneme: "#e8830c",
  maliyet: "#d94040", soluk: "#6b7793", cizgi: "#e3e8f2",
};

/* --------------------------- buyuk ozet karti ---------------------------
 * [EKLEME 2026-08-09] Kart artik TIKLANABILIR olabiliyor: onTikla verilirse
 * imlec el olur, ustune gelince kalkar ve "detayı gör ›" ipucu cikar.
 * onTikla VERILMEZSE kart eskisi gibi durur — MUSTERI satirindaki kartlar
 * bu yuzden hic degismedi. */
function OzetKart({ renk, baslik, buyuk, alt, ipucu, onTikla, acik }) {
  const tiklanir = typeof onTikla === "function";
  return (
    <Card
      elevation={0}
      onClick={tiklanir ? onTikla : undefined}
      role={tiklanir ? "button" : undefined}
      tabIndex={tiklanir ? 0 : undefined}
      onKeyDown={tiklanir ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTikla(); } } : undefined}
      sx={{
        minWidth: 210, flex: "1 1 210px", borderRadius: 3,
        border: "1px solid " + (acik ? renk : RENK.cizgi), overflow: "hidden", position: "relative",
        ...(tiklanir ? {
          cursor: "pointer",
          transition: "box-shadow .15s, transform .15s, border-color .15s",
          "&:hover": { boxShadow: "0 6px 18px rgba(20,30,60,.10)", transform: "translateY(-2px)", borderColor: renk },
          "&:focus-visible": { outline: "2px solid " + renk, outlineOffset: 2 },
        } : {}),
      }}
    >
      <Box sx={{ height: 4, bgcolor: renk }} />
      <CardContent sx={{ py: 2.2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: .7, textTransform: "uppercase", color: RENK.soluk }}>
          {baslik}
        </Typography>
        <Typography sx={{ fontSize: 27, fontWeight: 800, mt: .7, letterSpacing: -.8, lineHeight: 1.15 }}>
          {buyuk}
        </Typography>
        {alt ? <Typography sx={{ fontSize: 12.5, color: RENK.soluk, mt: .4 }}>{alt}</Typography> : null}
        {ipucu ? <Typography sx={{ fontSize: 11.5, color: RENK.soluk, mt: .9, fontStyle: "italic" }}>{ipucu}</Typography> : null}
        {tiklanir ? (
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: renk, mt: 1 }}>
            {acik ? "▼ detay açık — kapatmak için tıklayın" : "detayı gör ›"}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* --------------------------- grafik cercevesi --------------------------- */
function GrafikBlok({ baslik, aciklama, children, yukseklik = 300 }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid " + RENK.cizgi, mb: 3 }}>
      <CardContent>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{baslik}</Typography>
        <Typography sx={{ fontSize: 13, color: RENK.soluk, mb: 2 }}>{aciklama}</Typography>
        <Box sx={{ width: "100%", height: yukseklik }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ============================== ANA BILESEN ============================== */
export default function PatronPanosu() {
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState("");
  const [yukleniyor, setYukleniyor] = useState(true);

  const [aktifListe, setAktifListe] = useState(null);
  const [liste, setListe] = useState(null);
  const [listeYukleniyor, setListeYukleniyor] = useState(false);
  const [donem, setDonem] = useState("ay");
  // [EKLEME 2026-08-09] Hangi PARA karti acik — kartin cercevesini renklendirmek
  // ve tekrar tiklaninca kapatmak icin. Alttaki 6 dugme bundan ETKILENMEZ.
  const [aktifKart, setAktifKart] = useState(null);
  const detayRef = useRef(null);

  // [DUZELTME 2026-08-09] Panel, giriste "beni hatirla" ISARETLI DEGILSE token'i
  // sessionStorage'a yaziyor (LoginPage.jsx:63); isaretliyse localStorage'a (satir 58).
  // Ilk surumde SADECE localStorage'a bakiyordum -> token bulunamiyor, 401 aliniyordu.
  // Artik panelin diger 12 sayfasiyla BIREBIR AYNI sekilde ikisine de bakiyor.
  const basliklar = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return t ? { Authorization: "Bearer " + t } : {};
  }, []);

  /* --------- ozet --------- */
  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const b = basliklar();
        if (!b.Authorization) throw new Error("Giriş bilgisi bulunamadı. Panelden çıkıp tekrar giriş yapın.");
        const r = await fetch("/api/dashboard/patron", { headers: b });
        if (r.status === 401) throw new Error("Oturum süresi dolmuş, tekrar giriş yapın.");
        if (r.status === 403) throw new Error("Bu bölümü sadece yönetici görebilir.");
        if (!r.ok) throw new Error("Sunucu hatası (" + r.status + ")");
        const d = await r.json();
        if (!iptal) { setVeri(d); setYukleniyor(false); }
      } catch (e) {
        if (!iptal) { setHata(e.message); setYukleniyor(false); }
      }
    })();
    return () => { iptal = true; };
  }, [basliklar]);

  /* --------- affiliate / kupon raporu --------- [EKLEME 2026-08-10 — Ercan onayi]
   * Kod bazli satis tablosu: hangi kuponla kac abone/deneme/iptal + tahsilat.
   * Uc: /api/dashboard/patron/affiliate (sadece admin). */
  const [aff, setAff] = useState(null);
  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const b = basliklar();
        if (!b.Authorization) return;
        const r = await fetch("/api/dashboard/patron/affiliate", { headers: b });
        if (!r.ok) return;
        const d = await r.json();
        if (!iptal && d?.success) setAff(d.data);
      } catch (e) { /* rapor yuklenemezse pano calismaya devam eder */ }
    })();
    return () => { iptal = true; };
  }, [basliklar]);

  /* --------- detay listesi --------- */
  const listeAc = useCallback(async (tur, yeniDonem) => {
    const d = yeniDonem || donem;
    if (aktifListe === tur && !yeniDonem) { setAktifListe(null); setListe(null); return; }
    setAktifListe(tur); setListeYukleniyor(true); setListe(null);
    try {
      const r = await fetch("/api/dashboard/patron/liste?tur=" + tur + "&donem=" + d, { headers: basliklar() });
      if (!r.ok) throw new Error("Liste alınamadı (" + r.status + ")");
      setListe(await r.json());
    } catch (e) {
      setListe({ baslik: "Hata", aciklama: e.message, sutunlar: [], satirlar: [] });
    } finally { setListeYukleniyor(false); }
  }, [aktifListe, donem, basliklar]);

  /* --------- PARA kartina tiklama --------- [EKLEME 2026-08-09]
   * Kartin rakami hangi araliga aitse listeyi TAM O ARALIKLA acar
   * (ornek: "Gecen ay ciro" -> donem=gecenay). Boylece tablodaki satirlarin
   * toplami kartta yazan rakamla tutar. Ayni karta tekrar tiklamak kapatir. */
  const kartAc = useCallback((kart, tur, d) => {
    if (aktifKart === kart) {
      setAktifKart(null); setAktifListe(null); setListe(null);
      return;
    }
    setAktifKart(kart);
    setDonem(d);
    listeAc(tur, d);
    setTimeout(() => {
      if (detayRef.current && detayRef.current.scrollIntoView) {
        detayRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
  }, [aktifKart, listeAc]);

  if (yukleniyor) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <CircularProgress size={26} />
        <Typography sx={{ mt: 1.5, color: RENK.soluk, fontSize: 14 }}>Patron özeti yükleniyor…</Typography>
      </Box>
    );
  }
  if (hata) return <Alert severity="warning" sx={{ mb: 3 }}>Patron özeti açılamadı: {hata}</Alert>;
  if (!veri) return null;

  const o = veri.ozet;
  const ayFarki = o.ciro.buAy.tutar - o.ciro.gecenAy.tutar;

  /* --------- butonlar --------- */
  const butonlar = [
    { tur: "odemeler", etiket: "💰 Alınan ödemeler", adet: o.ciro.buAy.adet, renk: RENK.ciro },
    { tur: "kayitlar", etiket: "🙋 Yeni kayıtlar", adet: o.kayit.buAy, renk: RENK.kayit },
    { tur: "deneme", etiket: "🎁 7 gün ücretsiz başlayanlar", adet: o.deneme.buAy, renk: RENK.deneme },
    { tur: "iptaller", etiket: "❌ Denemede iptal edenler", adet: o.deneme.erkenIptal, renk: RENK.maliyet },
    { tur: "basarisiz", etiket: "⚠️ Ödemesi alınamayanlar", adet: o.kacanGelir.adet, renk: RENK.maliyet },
    { tur: "bedava", etiket: "🆓 Ücretsiz verilenler", adet: o.bedava.buAy, renk: RENK.soluk },
  ];

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mt: 2, mb: .5 }}>
        👔 Patron Özeti
      </Typography>
      <Typography textAlign="center" sx={{ color: RENK.soluk, fontSize: 13.5, mb: 2.5 }}>
        Sadece <b>gerçekten para geçen</b> işlemler "ciro" sayılır. Ücretsiz açılan erişimler ayrı gösterilir.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* ---------- PARA ---------- */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: RENK.soluk, letterSpacing: .6, mb: 1.2 }}>
        PARA
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <OzetKart
          renk={RENK.ciro} baslik="Bu ay ciro" buyuk={TL(o.ciro.buAy.tutar)}
          alt={SAY(o.ciro.buAy.adet) + " ödeme"}
          ipucu={ayFarki === 0 ? "geçen ayla aynı"
            : (ayFarki > 0 ? "geçen aya göre +" + TL(ayFarki) : "geçen aya göre " + TL(ayFarki))}
          acik={aktifKart === "ciroBuAy"}
          onTikla={() => kartAc("ciroBuAy", "odemeler", "ay")}
        />
        <OzetKart renk={RENK.soluk} baslik="Geçen ay ciro" buyuk={TL(o.ciro.gecenAy.tutar)}
          alt={SAY(o.ciro.gecenAy.adet) + " ödeme"}
          acik={aktifKart === "ciroGecenAy"}
          onTikla={() => kartAc("ciroGecenAy", "odemeler", "gecenay")} />
        <OzetKart renk={RENK.ciro} baslik="Bu yıl toplam ciro" buyuk={TL(o.ciro.buYil.tutar)}
          alt={SAY(o.ciro.buYil.adet) + " ödeme"}
          acik={aktifKart === "ciroBuYil"}
          onTikla={() => kartAc("ciroBuYil", "odemeler", "yil")} />
        <OzetKart renk={RENK.maliyet} baslik="Kaçan gelir" buyuk={TL(o.kacanGelir.tutar)}
          alt={SAY(o.kacanGelir.adet) + " kişiden para alınamadı"}
          ipucu="kartı geçmeyen müşteriler — geri kazanılabilir"
          acik={aktifKart === "kacanGelir"}
          onTikla={() => kartAc("kacanGelir", "basarisiz", "tum")} />
        <OzetKart renk={RENK.maliyet} baslik="Bu ay yapay zekâ gideri" buyuk={TL2(o.aiMaliyet.buAy.tl)}
          alt={"toplam " + TL2(o.aiMaliyet.toplam.tl)}
          ipucu="ölçüm 09.08.2026'da başladı"
          acik={aktifKart === "aiMaliyet"}
          onTikla={() => kartAc("aiMaliyet", "aimaliyet", "ay")} />
      </Stack>

      {/* ---------- MÜŞTERİ ---------- */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: RENK.soluk, letterSpacing: .6, mb: 1.2 }}>
        MÜŞTERİ
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <OzetKart renk={RENK.kayit} baslik="Bu ay yeni kayıt" buyuk={SAY(o.kayit.buAy)}
          alt={"geçen ay " + SAY(o.kayit.gecenAy)} />
        <OzetKart renk={RENK.kayit} baslik="Toplam kullanıcı" buyuk={SAY(o.kayit.toplam)}
          alt="yönetici hesapları hariç" ipucu="aşağıdaki Genel İstatistikler ile aynı sayı" />
        <OzetKart renk={RENK.ciro} baslik="Aktif aboneliği olan" buyuk={SAY(o.aktifAbone)}
          alt="süresi devam eden" />
        <OzetKart renk={RENK.deneme} baslik="Şu an denemede" buyuk={SAY(o.deneme.durumlar.trialing || 0)}
          alt="7 gün ücretsiz kullanıyor" ipucu="bunlar yakında ödeyecek" />
        <OzetKart renk={RENK.maliyet} baslik="Denemede vazgeçen" buyuk={SAY(o.deneme.erkenIptal)}
          alt={"toplam " + SAY(o.deneme.toplam) + " denemeden"} />
      </Stack>

      {/* ---------- DÜRÜSTLÜK NOTLARI ---------- */}
      {veri.uyarilar && veri.uyarilar.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          <b>Bu rakamları okurken bilmeniz gerekenler:</b>
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {veri.uyarilar.map((u, i) => (
              <li key={i}><Typography sx={{ fontSize: 13 }}>{u}</Typography></li>
            ))}
          </Box>
        </Alert>
      )}

      {/* ---------- GRAFİK: AYLIK CİRO ---------- */}
      <GrafikBlok
        baslik="Aylık ciro (son 12 ay)"
        aciklama="Her sütun o ay kasaya giren gerçek parayı gösterir. Ücretsiz açılan erişimler bu grafiğe dahil değildir."
      >
        <RcBarChart data={veri.aylik} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={RENK.cizgi} vertical={false} />
          <XAxis dataKey="etiket" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v >= 1000 ? (v / 1000) + "b" : v} />
          <RcTooltip formatter={(v, ad) => ad === "ciro" ? [TL(v), "Ciro"] : [SAY(v), "Ödeme adedi"]} />
          <RcLegend formatter={(v) => v === "ciro" ? "Ciro (₺)" : "Ödeme adedi"} />
          <Bar dataKey="ciro" fill={RENK.ciro} radius={[5, 5, 0, 0]} maxBarSize={54} />
        </RcBarChart>
      </GrafikBlok>

      {/* ---------- GRAFİK: KAYIT vs DENEME ---------- */}
      <GrafikBlok
        baslik="Aylık yeni kayıt ve ücretsiz deneme"
        aciklama="Mavi sütun: o ay kaç kişi kaydoldu. Turuncu sütun: kaç kişi 7 gün ücretsiz denemeyi başlattı. Turuncu maviye ne kadar yakınsa, kaydolanları denemeye o kadar iyi çeviriyoruz demektir."
      >
        <RcBarChart data={veri.aylik} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={RENK.cizgi} vertical={false} />
          <XAxis dataKey="etiket" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
          <RcTooltip formatter={(v, ad) => [SAY(v), ad === "kayit" ? "Yeni kayıt" : "Ücretsiz deneme"]} />
          <RcLegend formatter={(v) => v === "kayit" ? "Yeni kayıt" : "Ücretsiz deneme başlatan"} />
          <Bar dataKey="kayit" fill={RENK.kayit} radius={[5, 5, 0, 0]} maxBarSize={34} />
          <Bar dataKey="deneme" fill={RENK.deneme} radius={[5, 5, 0, 0]} maxBarSize={34} />
        </RcBarChart>
      </GrafikBlok>

      {/* ---------- GRAFİK: YILLIK ---------- */}
      <GrafikBlok
        baslik="Yıllık toplam"
        aciklama="Yıl yıl ciro ve kaydolan kişi sayısı. Uzun vadede büyüyor muyuz, tek bakışta buradan görülür."
        yukseklik={260}
      >
        <RcBarChart data={veri.yillik} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={RENK.cizgi} vertical={false} />
          <XAxis dataKey="yil" tick={{ fontSize: 13 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v >= 1000 ? (v / 1000) + "b" : v} />
          <RcTooltip formatter={(v, ad) => ad === "ciro" ? [TL(v), "Ciro"] : [SAY(v), "Yeni kayıt"]} />
          <RcLegend formatter={(v) => v === "ciro" ? "Ciro (₺)" : "Yeni kayıt"} />
          <Bar dataKey="ciro" fill={RENK.ciro} radius={[5, 5, 0, 0]} maxBarSize={80} />
          <Bar dataKey="kayit" fill={RENK.kayit} radius={[5, 5, 0, 0]} maxBarSize={80} />
        </RcBarChart>
      </GrafikBlok>

      {/* ---------- DETAY BUTONLARI ---------- */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid " + RENK.cizgi, mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Detayları gör</Typography>
          <Typography sx={{ fontSize: 13, color: RENK.soluk, mb: 2 }}>
            Bir başlığa tıklayın; kimler, ne zaman, hangi paket, ne kadar — hepsi liste halinde açılır.
          </Typography>

          <Stack direction="row" spacing={1.2} sx={{ mb: 2 }}>
            {[["hafta", "Son 7 gün"], ["ay", "Bu ay"], ["tum", "Başından beri"]].map(([d, ad]) => (
              <Chip
                key={d} label={ad} size="small"
                color={donem === d ? "primary" : "default"}
                variant={donem === d ? "filled" : "outlined"}
                onClick={() => { setAktifKart(null); setDonem(d); if (aktifListe) listeAc(aktifListe, d); }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {butonlar.map((b) => (
              <Button
                key={b.tur}
                variant={aktifListe === b.tur ? "contained" : "outlined"}
                onClick={() => { setAktifKart(null); listeAc(b.tur); }}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13.5,
                  borderColor: b.renk, color: aktifListe === b.tur ? "#fff" : b.renk,
                  bgcolor: aktifListe === b.tur ? b.renk : "transparent",
                  "&:hover": { bgcolor: aktifListe === b.tur ? b.renk : b.renk + "14", borderColor: b.renk },
                }}
              >
                {b.etiket}
                <Box component="span" sx={{
                  ml: 1, px: .9, py: .1, borderRadius: 99, fontSize: 12, fontWeight: 800,
                  bgcolor: aktifListe === b.tur ? "rgba(255,255,255,.25)" : b.renk + "1f",
                }}>{SAY(b.adet)}</Box>
              </Button>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* ---------- DETAY TABLOSU ---------- */}
      {aktifListe && (
        <Card ref={detayRef} elevation={0} sx={{ borderRadius: 3, border: "1px solid " + RENK.cizgi, mb: 3 }}>
          <CardContent>
            {listeYukleniyor ? (
              <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>
            ) : liste ? (
              <>
                <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{liste.baslik}</Typography>
                  {/* [EKLEME 2026-08-09] Hangi donemi gordugu HER ZAMAN yazili olsun ki
                      karttaki rakamla tablo karistirilmasin. */}
                  {liste.donemAdi ? (
                    <Chip size="small" label={"Dönem: " + liste.donemAdi} variant="outlined" />
                  ) : null}
                  <Chip size="small" label={SAY(liste.adet) + " kayıt"} variant="outlined" />
                </Stack>
                <Typography sx={{ fontSize: 13, color: RENK.soluk, mt: .8, mb: 2 }}>{liste.aciklama}</Typography>
                {liste.satirlar.length === 0 ? (
                  <Typography sx={{ color: RENK.soluk, py: 3, fontSize: 14 }}>
                    Bu dönemde kayıt yok. Üstteki "Başından beri" seçeneğini deneyin.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {liste.sutunlar.map((s, i) => (
                            <TableCell key={i} sx={{ fontWeight: 700, fontSize: 12, color: RENK.soluk, whiteSpace: "nowrap" }}>
                              {s}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {liste.satirlar.map((satir, i) => (
                          <TableRow key={i} hover>
                            {satir.map((h, j) => {
                              let icerik;
                              if (h && typeof h === "object" && h.tl !== undefined) {
                                icerik = <b>{TL(h.tl)}</b>;
                              } else if (h && typeof h === "object" && "tarih" in h) {
                                icerik = TARIH(h.tarih);
                              } else {
                                const s = String(h == null ? "-" : h);
                                icerik = /ALINDI|PARA ÖDEDİ/.test(s)
                                  ? <Chip size="small" label={s} sx={{ bgcolor: RENK.ciro + "1f", color: RENK.ciro, fontWeight: 700 }} />
                                  : /iptal|ALINAMADI|gecikti/.test(s)
                                    ? <Chip size="small" label={s} sx={{ bgcolor: RENK.maliyet + "1f", color: RENK.maliyet, fontWeight: 700 }} />
                                    : /deneme|ücretsiz/.test(s)
                                      ? <Chip size="small" label={s} sx={{ bgcolor: RENK.deneme + "1f", color: RENK.deneme, fontWeight: 700 }} />
                                      : s;
                              }
                              return <TableCell key={j} sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{icerik}</TableCell>;
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                <Typography sx={{ fontSize: 12, color: RENK.soluk, mt: 1.5 }}>
                  {SAY(liste.adet)} kayıt{liste.sinir ? " · " + liste.sinir : ""}
                </Typography>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* [EKLEME 2026-08-10] AFFILIATE / KUPON SATIS TABLOSU — Ercan onayi */}
      {aff?.satirlar?.length > 0 && (
        <Card sx={{ mt: 4, borderRadius: 3 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>
              🤝 Affiliate / Kupon Satışları
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
              Hangi kod kaç satış getirdi — komisyon hesabının temeli. Tahsilat = gerçekleşen çekimler.
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Kod", "Kampanya", "Aktif Abone", "Deneme", "İptal", "Başarısız Çekim", "Tahsilat", "Durum"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aff.satirlar.map((s) => (
                    <TableRow key={s.kod} hover>
                      <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>{s.kod}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.kampanyaAdi || "-"}</TableCell>
                      <TableCell>{SAY(s.aktifAbone)}</TableCell>
                      <TableCell>{SAY(s.deneme)}</TableCell>
                      <TableCell>{SAY(s.iptal)}</TableCell>
                      <TableCell>{SAY(s.basarisiz)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{TL(s.tahsilatTL)}</TableCell>
                      <TableCell>
                        {s.aktifMi
                          ? <Chip size="small" label="açık" color="success" variant="outlined" />
                          : <Chip size="small" label="kapalı" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 4 }} />
    </Box>
  );
}
